import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { setStorageValue } from 'App/Helpers';
import { ConsentContext } from 'App/Providers/games/ConsentProvider';

import { getPushStatus, isWebPushAvailable, resolvePushStatus } from './helpers';
import { PushPhaseGate } from './PushPhaseGate';

const PushContext = createContext(null);

/**
 * Shared push provider. Exposes push state plus push-service readiness,
 * which `useOneSignalInit` publishes from below `AppProvider` (where the
 * OneSignal app id lives). Pass `enablePhase` for LPs that show PushPhase
 * (web); desktop/mobile omit it so phase hooks never mount.
 *
 * @param {object} props - Component props.
 * @param {string} props.storageKey - Base storage namespace for push values.
 * @param {boolean} [props.enablePhase=false] - When true, mounts {@link PushPhaseGate}.
 * @param {import("react").ReactNode} props.children - Landing tree that reads push state through `PushContext`.
 * @returns {import("react").JSX.Element} - The context provider wrapping `children`, plus the phase gate when enabled.
 */
const PushProvider = ({ storageKey, children, enablePhase = false }) => {
    const { functionalConsent } = useContext(ConsentContext);

    const [isPushAvailable] = useState(() => isWebPushAvailable());
    const [storedStatus, setStoredStatus] = useState(() =>
        isPushAvailable ? getPushStatus(storageKey) : 'notAvailable'
    );
    const [pushServiceReady, setPushServiceReady] = useState(false);

    // Consent is applied here rather than written back to storage, so the
    // stored value stays a record of the user's own push decision and a
    // consent-off visit cannot leave a 'denied' behind for the next one.
    const pushStatus = resolvePushStatus({
        storedStatus,
        isPushAvailable,
        functionalConsent,
    });

    // Availability and the stored value both resolve synchronously at mount,
    // so consent is the only input that arrives late. An unavailable device is
    // therefore final immediately - consent cannot change 'notAvailable' -
    // while everywhere else the status stays provisional until consent lands.
    // Consumers that cannot revise their decision afterwards - GameAnalytics
    // seeds a custom dimension that no longer applies to events already sent -
    // must wait for this rather than assume consent is settled.
    const consentResolved = typeof functionalConsent === 'boolean';
    const pushStatusReady = isPushAvailable ? consentResolved : true;

    const updatePushStatus = useCallback(
        (value) => {
            setStoredStatus(value);
            setStorageValue(storageKey, 'push.status', value);
        },
        [storageKey]
    );

    const contextValue = useMemo(
        () => ({
            pushStatus,
            pushStatusReady,
            updatePushStatus,
            isPushAvailable,
            pushServiceReady,
            setPushServiceReady,
        }),
        [
            pushStatus,
            pushStatusReady,
            updatePushStatus,
            isPushAvailable,
            pushServiceReady,
            setPushServiceReady,
        ]
    );

    if (enablePhase) {
        return (
            <PushPhaseGate storageKey={storageKey} contextValue={contextValue}>
                {(phaseContextValue) => (
                    <PushContext value={phaseContextValue}>{children}</PushContext>
                )}
            </PushPhaseGate>
        );
    }

    return <PushContext value={contextValue}>{children}</PushContext>;
};

export { PushContext, PushProvider };
